/**
 * SES-1's **confirming hold**, as a framework-free controller.
 *
 * ── Why this is not inside the component ─────────────────────────────────────
 *
 * §17.1's confirming-hold row is the most demanding acceptance row in the specification:
 *
 * > each of Kickoff, Half, Kickoff 2H and Full Time completes only after `TRANSITION_HOLD_MS` of
 * > sustained press, and the written anchor equals the timestamp captured at **press-start** —
 * > asserted to the millisecond against a fake clock, for all four … releasing before
 * > `TRANSITION_HOLD_MS` writes **no** anchor, dispatches **no** command and changes **no** state;
 * > the anchor is **never** the completion time (a test that holds for 3× the duration must still
 * > write press-start).
 *
 * `vitest.config.ts` runs `environment: 'node'` and nothing in this repo renders a React tree, so a
 * hold that lived inside a component could only be asserted through a browser — and a browser cannot
 * hold a fake clock still to the millisecond. The clock and the timer are therefore **injected** and
 * the rules live here, where a Node suite drives every one of them. `TransitionControl.tsx` is the
 * adapter that hands it real pointer events.
 *
 * ── The anchor rule, and the failure it is guarding against ──────────────────
 *
 * Design §2.2:
 *
 * ```
 * pointerdown  → tPress = Date.now()        // captured here, nothing written
 *              → begin the fill animation
 * [hold]       → at TRANSITION_HOLD_MS, dispatch Kickoff{ anchorTs: tPress }
 * pointerup    → before completion: cancel. No command, no anchor, no state change.
 * ```
 *
 * The value carried forward is the one captured **when the trigger fired**, never re-derived when
 * the effect runs. Stamping completion time instead would shift the clock forward by the hold
 * duration at every one of four anchors, compounding — *"with no visible symptom"*. That is why
 * `now` is read exactly once, in {@link ConfirmingHold.press}, and why the completion path has no
 * access to a clock at all: it can only forward what press already captured.
 *
 * ── Completion is driven by the timer, not by the release ────────────────────
 *
 * A version that completed on `pointerup` (having checked the elapsed time) would satisfy "only
 * after `TRANSITION_HOLD_MS`" and still be wrong: a 3× hold would then dispatch an anchor
 * `3 × TRANSITION_HOLD_MS` old, and `machine.transition` would reject it as outside
 * `[0, TRANSITION_HOLD_MS + HOLD_SLOP_MS]`. Firing from the timer keeps the anchor's age bounded by
 * the hold itself, which is exactly what `HOLD_SLOP_MS` was sized for.
 */

export interface ConfirmingHoldDeps {
  /** `config.TRANSITION_HOLD_MS`. Passed rather than imported so the control and the fill agree. */
  readonly holdMs: number;
  /** Wall clock. Read **once** per press, at `pointerdown`. */
  readonly now: () => number;
  readonly setTimer: (fn: () => void, ms: number) => number;
  readonly clearTimer: (id: number) => void;
  /** Called at completion with the **press-start** timestamp. Never called for a cancelled press. */
  readonly onComplete: (pressStartTs: number) => void;
  /** Drives the fill affordance. Fires `true` at press-start and `false` at completion or cancel. */
  readonly onHoldingChange?: (holding: boolean) => void;
}

export interface ConfirmingHold {
  /** `pointerdown`. Captures press-start and arms the hold. A second press while held is ignored. */
  press(): void;
  /** `pointerup` / `pointercancel` / `pointerleave`. Before completion this writes nothing at all. */
  cancel(): void;
  /** Unmount. Same as {@link cancel} — an armed hold must not fire against a torn-down tree. */
  dispose(): void;
  holding(): boolean;
}

export function createConfirmingHold(deps: ConfirmingHoldDeps): ConfirmingHold {
  let timer: number | undefined;
  let pressStartTs: number | null = null;

  /** Disarms and reports. The single exit from the holding state, so the fill can never stick on. */
  const stop = (): void => {
    if (timer !== undefined) deps.clearTimer(timer);
    timer = undefined;
    pressStartTs = null;
    deps.onHoldingChange?.(false);
  };

  return {
    press(): void {
      // A second contact must not restart the fill or arm a second timer: two fingers on one control
      // are one press, and re-reading the clock here would move the anchor forward mid-hold.
      if (timer !== undefined) return;

      const startedAt = deps.now();
      pressStartTs = startedAt;
      deps.onHoldingChange?.(true);
      timer = deps.setTimer(() => {
        timer = undefined;
        const anchorTs = pressStartTs;
        pressStartTs = null;
        deps.onHoldingChange?.(false);
        // `startedAt` is in scope and identical; `pressStartTs` is forwarded instead so that any
        // future path that clears it (a cancel racing the timer) cannot dispatch a stale anchor.
        if (anchorTs !== null) deps.onComplete(anchorTs);
      }, deps.holdMs);
    },

    cancel(): void {
      if (timer === undefined && pressStartTs === null) return;
      // Deliberately nothing but `stop()`. §2.2: "Release before completion cancels silently."
      stop();
    },

    dispose(): void {
      if (timer === undefined && pressStartTs === null) return;
      stop();
    },

    holding(): boolean {
      return timer !== undefined;
    },
  };
}
